import { useEffect, useState } from "react";
import { ShieldOff, X } from "lucide-react";

/**
 * Soft modal for reversible suspend/remove actions. Shows a reason textarea
 * (optional) and a single confirm button — no typed string required.
 *
 * Props:
 *   open, onClose, onConfirm({reason}), title, description, busy, testIdPrefix,
 *   confirmButtonLabel, requireReason
 */
export default function ConfirmSuspendModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  busy = false,
  confirmButtonLabel = "Confirm",
  requireReason = false,
  testIdPrefix = "confirm-suspend",
}) {
  const [reason, setReason] = useState("");

  useEffect(() => { if (open) setReason(""); }, [open]);

  if (!open) return null;

  const ready = !requireReason || reason.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid={`${testIdPrefix}-overlay`}
    >
      <div className="card-surface w-full max-w-md p-6 sm:p-8 relative" data-testid={`${testIdPrefix}-modal`}>
        <button onClick={onClose} disabled={busy} className="absolute top-4 right-4 text-white/40 hover:text-white" aria-label="Close" data-testid={`${testIdPrefix}-close`}>
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-yellow-500/15 border border-yellow-500/30">
            <ShieldOff className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-2xl font-bold leading-tight">{title}</h3>
            <div className="text-sm text-white/60 mt-2 space-y-2">{description}</div>
          </div>
        </div>

        <div className="mt-6">
          <label className="text-eyebrow block mb-2">Reason {requireReason ? "" : "(optional)"}</label>
          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="input-dark resize-none"
            placeholder="What's the reason? (saved to admin audit log)"
            maxLength={500}
            disabled={busy}
            data-testid={`${testIdPrefix}-reason`}
          />
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="ghost-btn" data-testid={`${testIdPrefix}-cancel`}>Cancel</button>
          <button
            onClick={() => onConfirm({ reason: reason.trim() })}
            disabled={!ready || busy}
            className="ember-btn bg-yellow-600 hover:bg-yellow-500 disabled:opacity-40"
            data-testid={`${testIdPrefix}-submit`}
          >
            {busy ? "Working..." : confirmButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
